import { LoginRegisterService } from './../../shared/services/login-register.service';
import { Component, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { MatError, MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { Router } from '@angular/router';
import { BusinessLoginInfo } from '../../shared/interfaces/business-login.model';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { STORAGE_KEY } from '../../shared/constants/store-key.constants';
import { NgClass } from '@angular/common';
import {
  companyNoValidator,
  empIdValidator,
} from '../../shared/utils/validator.util';
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { ApiResponse } from '../../shared/interfaces/api-response.model';
import { CheckEnterpriseAvailableTimeResponse } from '../../shared/interfaces/login-register.model';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import { BusinessPlanRequest } from '../../shared/interfaces/reserve-loading.model';
import { ID_LOGIN_TYPE } from '../../shared/constants/common.constants';
import { API_DATE_FORMAT } from '../../shared/constants/date.constants';


/**
 * 企業登入組件
 */
@Component({
  selector: 'app-business-login',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    ReactiveFormsModule,
    FormsModule,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatError,
    NgClass,
    MatCheckbox
],
  templateUrl: './business-login.component.html',
  styleUrl: './business-login.component.scss',
})
export class BusinessLoginComponent {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  translateService: TranslateService = inject(TranslateService);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);
  loginRegisterService: LoginRegisterService = inject(LoginRegisterService);
  toastr: ToastrService = inject(ToastrService);

  showError: boolean = false; // 是否顯示錯誤訊息
  isProcessing: boolean = false; // 是否正在處理提交

  companyNoMaxLength: number = 10; // 企業代碼最大長度
  idNumberMaxLength: number = 20; // 員工編號最大長度
  // fillIdNumberChecked: boolean = false; // 是否勾選帶入身分證號碼
  userIdNumber: string = ''; // 使用者身分證號碼

  controlItem: number = 0; // 控制項

  private currentFamilyFlag: string = ''; // 當前選擇的身份類型（員工或家屬）

  /**
   * (CB-039) 企業預約 flowCode
   * 0：可預約狀態
   * 4：已有預約資料，請登入
   * 5：已有已完成健檢，跳 dialog
   * 6：合約期間內員工編號已被使用
   * 7：可預約，但補助金額為 0
   * 8：該企業未開放網路預約
   */
  enterpriseCondition: string = '';

  // 企業登入表單
  businessLoginForm = new FormGroup({
    empId: new FormControl('', [
      // Validators.required,
      (control: AbstractControl): ValidationErrors | null => {
        // 如果是空值，不進行驗證 (選填)
        if (!control.value || control.value.trim() === '') {
          return null;
        }
        return empIdValidator(control as FormControl, this.translateService);
        // return empIdValidator(formControl as FormControl, this.translateService);
      },
    ]),
    identity: new FormControl(API_PARAMS.IDENTITY.EMPLOYEE, [
      Validators.required,
    ]),
  });

  ngOnInit() {

    const employeeId = this.sharedStorageService.get('idLoginRequest')?.employeeNumber ?? '';
    // 如果 employeeId 有值，則將其填入表單
    if (employeeId) {
      this.businessLoginForm.get('empId')?.setValue(employeeId);
    }
    // 取得控制項
    this.controlItem = this.sharedStorageService.get('controlItem') ?? 0;
    this.getUserIdNumber();
    
    // 接收從登入頁面傳來的企業代碼和預約代碼
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state as { enterpriseCode?: string; empId?: string; familyFlag?: string; };
    const empId = state?.empId || (window.history.state as any)?.empId;
    const familyFlag = state?.familyFlag || (window.history.state as any)?.familyFlag;

    if (empId) {
      this.businessLoginForm.get('empId')?.setValue(empId);
    }
    if (familyFlag) {
      this.businessLoginForm.get('identity')?.setValue(familyFlag);
    }
  }


  // 從 SharedStorage 取得使用者的身分證號碼
  getUserIdNumber() {
    // 從 createMemberRequest 取得（會員登入/註冊時）
    const createMemberRequest = this.sharedStorageService.get(STORAGE_KEY.CREATE_MEMBER_REQUEST);
    if (createMemberRequest?.idNo) {
      this.userIdNumber = createMemberRequest.idNo;
      return;
    }
    this.userIdNumber = '';
  }

  // 處理 checkbox 變更事件
  // onFillIdNumberChange(checked: boolean) {
  //   console.log('onFillIdNumberChange:', checked);
  //   this.fillIdNumberChecked = checked;
  //   const value = checked && this.userIdNumber ? this.userIdNumber : '';
  //   this.businessLoginForm.get('empId')?.setValue(value);
  // }

  // 取得企業代碼錯誤訊息
  getCompanyNoErrorMessage(): string {
    const control = this.businessLoginForm.get('companyNo');
    if (!control || !control.errors || !this.showError) return '';
    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant(
        'error.company-number-error.required'
      );
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 取得員工編號錯誤訊息
  getEmpIdErrorMessage(): string {
    const control = this.businessLoginForm.get('empId');
    if (!control || !control.errors || !this.showError) return '';
    const errors = control.errors;
    // if (errors['required']) {
    //   return this.translateService.instant('error.employee-id-error.required');
    // }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 檢查企業預約資料狀態
  checkEnterpriseAvailableTime(
    companyNo: string,
    employeeId: string,
    idNo: string,
    familyFlag: string
  ) {

    // 儲存familyFlag
    this.currentFamilyFlag = familyFlag;

    // CB-039
    this.loginRegisterService
      .checkEnterpriseAvailableTime(companyNo, employeeId, idNo, familyFlag)
      .subscribe({
        next: (res: ApiResponse<CheckEnterpriseAvailableTimeResponse>) => {
          if (res.statusCode === 'S0000') {
            const flowCode = res.data?.flowCode;
            
            if (flowCode === '0') {
              // 可預約狀態，設定企業相關資料並導向選擇地點頁面
              this.isProcessing = false;

              // 儲存企業登入相關信息
              const businessLoginInfo: BusinessLoginInfo = {
                companyNo: companyNo,
                empId: employeeId,
                identity: familyFlag,
              };
              this.sharedStorageService.set(
                STORAGE_KEY.BUSINESS_LOGIN_INFO,
                businessLoginInfo
              );

              // 設定預約類型為企業
              this.sharedStorageService.set(
                STORAGE_KEY.RESERVE_TYPE,
                ID_LOGIN_TYPE.ENTERPRISE
              );

              // 從 createMemberRequest 取得生日和性別資訊
              const createMemberRequest = this.sharedStorageService.get(
                STORAGE_KEY.CREATE_MEMBER_REQUEST
              );

              // 設定 userPlanRequest（企業預約需要）
              const userPlanRequest: UserPlanRequest = {
                orgNo: '',
                gender: createMemberRequest?.gender || '',
                birthday: createMemberRequest?.birthday || '',
                age: '',
                idNo: idNo,
                name: createMemberRequest?.name || '',
                cell: '',
                email: '',
                telH: '',
                telO: '',
                zipCode: '',
                history: [],
                family: [],
                subjectiveSymptoms: [],
                identityType: familyFlag,
                funcType: '1',
              };
              this.sharedStorageService.set(
                STORAGE_KEY.USER_PLAN_REQUEST, 
                userPlanRequest
              );

              // 設定 businessPlanRequest
              const businessPlanRequest: BusinessPlanRequest = {
                orgNo: '',
                employeeId: employeeId,
                companyNo: companyNo,
                gender: createMemberRequest?.gender || '',
              };
              this.sharedStorageService.set(
                STORAGE_KEY.BUSINESS_PLAN_REQUEST, 
                businessPlanRequest
              );

              this.router.navigate(['/healthcheck/reserve/location']).then();
            } else if (flowCode === '4') {
              // 已有預約資料，請登入
              this.enterpriseCondition = '4';
              this.isProcessing = false;
              this.openErrorDialog();
            } else if (flowCode === '5') {
              // 已有已完成健檢，跳 dialog
              this.enterpriseCondition = '5';
              this.isProcessing = false;
              this.openErrorDialog();
            } else if (flowCode === '6') {
              // 合約期間內員工編號已被使用
              this.enterpriseCondition = '6';
              this.isProcessing = false;
              this.openErrorDialog();
            } else if (flowCode === '7') {
              // 可預約，但補助金額為 0
              this.enterpriseCondition = '7';
              this.isProcessing = false;
              // 儲存企業相關資訊
              // 儲存企業登入相關信息
              const businessLoginInfo: BusinessLoginInfo = {
                companyNo: companyNo,
                empId: employeeId,
                identity: familyFlag,
              };
              this.sharedStorageService.set(
                STORAGE_KEY.BUSINESS_LOGIN_INFO,
                businessLoginInfo
              );

              // 設定預約類型為企業
              this.sharedStorageService.set(
                STORAGE_KEY.RESERVE_TYPE,
                ID_LOGIN_TYPE.ENTERPRISE
              );

              // 從 createMemberRequest 取得生日和性別資訊
              const createMemberRequest = this.sharedStorageService.get(
                STORAGE_KEY.CREATE_MEMBER_REQUEST
              );

              // 設定 userPlanRequest（企業預約需要）
              const userPlanRequest: UserPlanRequest = {
                orgNo: '',
                gender: createMemberRequest?.gender || '',
                birthday: createMemberRequest?.birthday || '',
                age: '',
                idNo: idNo,
                name: createMemberRequest?.name || '',
                cell: '',
                email: '',
                telH: '',
                telO: '',
                zipCode: '',
                history: [],
                family: [],
                subjectiveSymptoms: [],
                identityType: familyFlag,
                funcType: '1',
              };
              this.sharedStorageService.set(
                STORAGE_KEY.USER_PLAN_REQUEST, 
                userPlanRequest
              );

              // 設定 businessPlanRequest
              const businessPlanRequest: BusinessPlanRequest = {
                orgNo: '',
                employeeId: employeeId,
                companyNo: companyNo,
                gender: createMemberRequest?.gender || '',
              };
              this.sharedStorageService.set(
                STORAGE_KEY.BUSINESS_PLAN_REQUEST, 
                businessPlanRequest
              );
              this.openErrorDialog();
            }
            // 該企業未開放網路預約
            else if (flowCode === '8') {
              this.enterpriseCondition = '8';
              this.isProcessing = false;
              this.openErrorDialog();
            }
          }
          // 只要查不到識別碼，一律跳出補助金額為 0 dialog
          else {
            // API 回傳錯誤 (EL1007、EL1013)
            this.isProcessing = false;
            // 顯示錯誤訊息的 Dialog
            this.dialog.open(SingleActionDialogComponent, {
            disableClose: true,
            data: {
              title: this.translateService.instant('id-login.sponsor-amt-zero.title'),
              message: this.translateService.instant('id-login.sponsor-amt-zero.message'),
              showCloseButton: false,
              buttonText: this.translateService.instant('common.button.confirm'),
              action: () => {
                // 儲存企業登入相關信息
              const businessLoginInfo: BusinessLoginInfo = {
                companyNo: companyNo,
                empId: employeeId,
                identity: familyFlag,
              };
              this.sharedStorageService.set(
                STORAGE_KEY.BUSINESS_LOGIN_INFO,
                businessLoginInfo
              );

              // 設定預約類型為企業
              this.sharedStorageService.set(
                STORAGE_KEY.RESERVE_TYPE,
                ID_LOGIN_TYPE.ENTERPRISE
              );

              // 從 createMemberRequest 取得生日和性別資訊
              const createMemberRequest = this.sharedStorageService.get(
                STORAGE_KEY.CREATE_MEMBER_REQUEST
              );

              // 設定 userPlanRequest（企業預約需要）
              const userPlanRequest: UserPlanRequest = {
                orgNo: '',
                gender: createMemberRequest?.gender || '',
                birthday: createMemberRequest?.birthday || '',
                age: '',
                idNo: idNo,
                name: createMemberRequest?.name || '',
                cell: '',
                email: '',
                telH: '',
                telO: '',
                zipCode: '',
                history: [],
                family: [],
                subjectiveSymptoms: [],
                identityType: familyFlag,
                funcType: '1',
              };
              this.sharedStorageService.set(
                STORAGE_KEY.USER_PLAN_REQUEST, 
                userPlanRequest
              );

              // 設定 businessPlanRequest
              const businessPlanRequest: BusinessPlanRequest = {
                orgNo: '',
                employeeId: employeeId,
                companyNo: companyNo,
                gender: createMemberRequest?.gender || '',
              };
              this.sharedStorageService.set(
                STORAGE_KEY.BUSINESS_PLAN_REQUEST, 
                businessPlanRequest
              );
                this.closeDialog();
                this.router.navigate(['/healthcheck/reserve/location']);
              },
            },
          });
        }
      },
        error: (error: any) => {
          console.error('Check enterprise available time error:', error);
          this.isProcessing = false;
          this.toastr.error(
            this.translateService.instant('error.system-error')
          );
        }
      });
  }

  // 對應顯示的 Dialog
  openErrorDialog() {
    let title: string = '';
    let message: string = '';
    let buttonText: string = this.translateService.instant(
      'common.button.i-understood'
    );
    let showCloseButton: boolean = true;

    if (this.enterpriseCondition === '4') {
      // 已有預約資料，請登入
      title = this.translateService.instant(
        'business-login.already-reserved.title'
      );
      message = this.translateService.instant(
        'business-login.already-reserved.message'
      );
      buttonText = this.translateService.instant(
        'business-login.go-to-member-center'
      );
      showCloseButton = false;
    } else if (this.enterpriseCondition === '5') {
      // 已有已完成健檢
      title = this.translateService.instant(
        'business-login.already-complete.title'
      );
      message = this.translateService.instant(
        'business-login.already-complete.message'
      );
      showCloseButton = true;
      this.sharedStorageService.clear();
      // 回首頁
      this.router.navigate(['/healthcheck']);
    } else if (this.enterpriseCondition === '6') {
      // 合約期間內員工編號已被使用
      title = this.translateService.instant(
        'id-login.employee-number-already-used.title'
      );
      message = this.translateService.instant(
        'id-login.employee-number-already-used.message'
      );
      showCloseButton = true;
    } else if (this.enterpriseCondition === '7') {
      // 可預約，但補助金額為 0
      title = this.translateService.instant(
        'id-login.sponsor-amt-zero.title'
      );
      message = this.translateService.instant(
        'id-login.sponsor-amt-zero.message'
      );
      buttonText = this.translateService.instant(
        'common.button.confirm'
      );
      showCloseButton = false;
    } else if (this.enterpriseCondition === '8') {
      // 員工
      if (this.currentFamilyFlag === API_PARAMS.IDENTITY.EMPLOYEE) {
        // 該企業未開放網路預約
        title = this.translateService.instant(
          'common.button.not-allow-reserve'
        );
        message = this.translateService.instant(
          'common.button.not-allow-worker-reserve-message'
        );
        showCloseButton = false;
      } else {
        // 該企業未開放網路預約
        title = this.translateService.instant(
          'common.button.not-allow-reserve'
        );
        message = this.translateService.instant(
          'common.button.not-allow-family-reserve-message'
        );
        showCloseButton = false;
      }
    }

    // 統一在這裡開啟 Dialog
    this.dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: title,
        message: message,
        showCloseButton: showCloseButton,
        buttonText: buttonText,
        action: () => {
          this.closeDialog();
          
          if (this.enterpriseCondition === '4') {
            // 導向會員中心 - 我的預約
            this.router.navigate(['/healthcheck/member/mybooking']);
          } else if (this.enterpriseCondition === '7') {
            // 補助金額為 0，但可預約，導航到選擇據點
            this.router.navigate(['/healthcheck/reserve/location']);
          } else {
            // 其他情況 (5, 6) 導向首頁
            this.sharedStorageService.clear();
            this.router.navigate(['/']);
          }
        },
      },
    });
  }

  // 關閉Dialog
  closeDialog() {
    this.dialog.closeAll();
  }

  // 開始預約
  onSubmit() {
    // 防止重複提交
    if (this.isProcessing) {
      return;
    }

    this.showError = true;

    if (this.businessLoginForm.valid) {
      this.isProcessing = true;

      const companyNo = this.sharedStorageService.get('companyNo') ?? '';
      const empId = this.businessLoginForm.get('empId')?.value?.trim() || '';
      const identity = this.businessLoginForm.get('identity')?.value || API_PARAMS.IDENTITY.EMPLOYEE;
      
      // 從 createMemberRequest 取得身分證字號
      const createMemberRequest = this.sharedStorageService.get(STORAGE_KEY.CREATE_MEMBER_REQUEST);
      const idNo = createMemberRequest?.idNo || '';

      // 如果沒有身分證字號，顯示錯誤
      if (!idNo) {
        this.isProcessing = false;
        this.toastr.error(
          this.translateService.instant('error.id-number-error.required')
        );
        return;
      }

      // 呼叫 CB-039 檢查企業預約資料狀態
      // employeeId 為選填，直接傳送使用者填寫的值（可能為空字串）
      this.checkEnterpriseAvailableTime(
        companyNo,
        empId,
        idNo,
        identity
      );
    }
  }

  protected readonly API_PARAMS = API_PARAMS;
}